/**
 * Zone fabric · camera bind · zone ops space (Soft≠Live).
 *
 * Spatial AI Command OS:
 *   Zone = spatial command volume
 *   Camera bind = Spatial Binding (coverage), NOT ESP=Camera physical forced 1:1
 *   Soft bindings persist 127-local only · Live zone rings still from GET /zones
 *
 * Browser Live player remains honest not_wired unless host wires WHEP later.
 */
import { isLocalHost } from "./pathseal.js";

const BIND_KEY = "croc_ops_zone_cam_bind_v1";
const SPACE_KEY = "croc_ops_zone_space_v1";

export function loadZoneCamBinds() {
  if (!isLocalHost()) return {};
  try {
    const raw = localStorage.getItem(BIND_KEY);
    const j = raw ? JSON.parse(raw) : {};
    return j && typeof j === "object" ? j : {};
  } catch {
    return {};
  }
}

export function saveZoneCamBinds(map) {
  if (!isLocalHost()) return;
  try {
    localStorage.setItem(BIND_KEY, JSON.stringify(map || {}));
  } catch {
    /* */
  }
}

/**
 * Persist last focused zone (map · Run scope).
 * Lab: localStorage Soft pad. Production: sessionStorage only (Live focus; no Soft invent).
 */
export function loadActiveZoneId() {
  try {
    if (isLocalHost()) {
      return String(localStorage.getItem(SPACE_KEY) || "").trim();
    }
    return String(sessionStorage.getItem(SPACE_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function saveActiveZoneId(id) {
  try {
    const store = isLocalHost() ? localStorage : sessionStorage;
    if (id) store.setItem(SPACE_KEY, String(id));
    else store.removeItem(SPACE_KEY);
  } catch {
    /* */
  }
}

/**
 * Catalogue for Ops · Zones desk + map.
 * Live zones preferred · Soft invent only when host is 127 and list empty.
 */
export function softZoneCatalogOf({ zones, devices, softState, centre }) {
  const live = Array.isArray(zones) ? zones : [];
  const binds = loadZoneCamBinds();
  const out = [];
  const seen = new Set();

  for (const z of live) {
    /* Live membership ids: zone_id first (server SoT); label is display-only. */
    const id = String(z.zone_id || z.id || z.label || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const lab = String(z.label || id).trim() || id;
    seen.add(lab);
    const cams = normalizeCamIds(binds[id] || binds[z.id] || binds[lab] || []);
    out.push({
      id,
      label: lab,
      kind: z.kind || "live",
      soft: !!z.soft,
      ring: z.ring || null,
      deviceIds: z.deviceIds || z.device_ids || [],
      count: Number(z.count || z.device_count || (z.deviceIds || z.device_ids || []).length || 0),
      cameraIds: cams,
      mismatch: z.mismatch || z.label_mismatch_device_ids || [],
      thumbSoft: true,
      livePlayer: "not_wired",
    });
  }

  /* Device zone labels as soft spatial buckets — lab only (127 Soft).
     Production Soft invents label-zones from inventory tags — Live rings only. */
  if (isLocalHost()) {
    const byLabel = new Map();
    for (const d of devices || []) {
      const lab = String(d && d.zone || "").trim();
      if (!lab || seen.has(lab)) continue;
      if (!byLabel.has(lab)) byLabel.set(lab, []);
      byLabel.get(lab).push(d.id);
    }
    for (const [lab, ids] of byLabel) {
      if (seen.has(lab)) continue;
      seen.add(lab);
      const cams = normalizeCamIds(binds[lab] || []);
      const pin = (devices || []).find((d) => d && ids.includes(d.id) && d.ll);
      out.push({
        id: lab,
        label: lab,
        kind: "label",
        soft: true,
        ring: null,
        deviceIds: ids,
        count: ids.length,
        cameraIds: cams,
        ll: pin && pin.ll,
        thumbSoft: true,
        livePlayer: "not_wired",
      });
    }
  }

  /* Soft theatre volumes · always available on 127 Soft (parallel to Live rings). */
  if (isLocalHost() && softState) {
    const origin = (softState.origin || centre || [101.6869, 3.139]).slice();
    const pad = [
      { id: "zone-north", label: "North yard", role: "yard", dLng: 0.00095, dLat: 0.00072, half: 0.00052 },
      { id: "zone-gate", label: "Main gate", role: "gate", dLng: -0.00062, dLat: 0.00018, half: 0.00038 },
      { id: "zone-roof", label: "Roof band", role: "roof", dLng: 0.00022, dLat: -0.00055, half: 0.00042 },
      { id: "zone-east", label: "East apron", role: "apron", dLng: 0.00088, dLat: -0.00012, half: 0.00048 },
    ];
    for (const p of pad) {
      if (seen.has(p.id) || seen.has(p.label)) continue;
      seen.add(p.id);
      seen.add(p.label);
      const ll = [origin[0] + p.dLng, origin[1] + p.dLat];
      const cams = normalizeCamIds(binds[p.id] || softPlantCamsNear(softState, ll, 3));
      out.push({
        id: p.id,
        label: p.label,
        role: p.role,
        kind: "soft_pad",
        soft: true,
        ring: softRectRing(ll, p.half),
        deviceIds: [],
        count: cams.length,
        cameraIds: cams,
        ll,
        thumbSoft: true,
        livePlayer: "not_wired",
      });
    }
  }

  return out.map((z) => softZoneVolumeVmOf(z, softState, devices));
}

/** Role tone for volume identity (map + desk). */
export function softZoneRoleOf(zone) {
  const z = zone && typeof zone === "object" ? zone : {};
  if (z.role) return String(z.role);
  const lab = String(z.label || z.id || "").toLowerCase();
  if (/gate|entry|door/.test(lab)) return "gate";
  if (/roof|sky|air/.test(lab)) return "roof";
  if (/dock|berth|pier/.test(lab)) return "dock";
  if (/load|bay|staging|warehouse/.test(lab)) return "loading";
  if (/apron|east|west|south|north|yard|lot/.test(lab)) return "yard";
  if (/perim|fence|bound/.test(lab)) return "perimeter";
  return z.soft ? "pad" : "ring";
}

const ROLE_META = {
  yard: { label: "Yard", tone: "aqua" },
  gate: { label: "Gate", tone: "amber" },
  roof: { label: "Roof", tone: "violet" },
  apron: { label: "Apron", tone: "mint" },
  dock: { label: "Dock", tone: "dock" },
  loading: { label: "Loading", tone: "lime" },
  perimeter: { label: "Ring", tone: "steel" },
  pad: { label: "Pad", tone: "amber" },
  ring: { label: "Volume", tone: "aqua" },
};

/**
 * Volume card VM for Volumes desk + map chips (pure).
 * Soft≠Live — cover counts are Bind / Soft observe, never Live player.
 */
export function softZoneVolumeVmOf(zone, softState, devices) {
  const z = zone && typeof zone === "object" ? { ...zone } : {};
  const role = softZoneRoleOf(z);
  const meta = ROLE_META[role] || ROLE_META.ring;
  const covers = normalizeCamIds(z.cameraIds || []).length;
  let plant = Array.isArray(z.deviceIds) ? z.deviceIds.length : 0;
  if (!plant && softState && z.ll) {
    const assets = softState.assets || [];
    plant = assets.filter((a) => a && a.fixed && a.ll && haversineM(z.ll, a.ll) < 90).length;
  }
  const unitsNear =
    softState && z.ll
      ? (softState.assets || []).filter(
          (a) => a && a.ll && !a.fixed && a.kind !== "device" && haversineM(z.ll, a.ll) < 95,
        ).length
      : 0;
  /* Lab Soft: cover bind readiness. Production Live: plant membership readiness. */
  let ready = "open";
  if (covers > 0) {
    ready = covers >= 2 ? "ready" : "thin";
  } else if (!z.soft && plant > 0) {
    ready = plant >= 2 ? "ready" : "thin";
  }
  let readyLabel = "Open";
  if (covers > 0) {
    readyLabel = ready === "ready" ? "Covered" : "Thin cover";
  } else if (!z.soft && plant > 0) {
    readyLabel = ready === "ready" ? "Plant live" : "Thin plant";
  }
  return {
    ...z,
    role,
    roleLabel: meta.label,
    tone: meta.tone,
    coverN: covers,
    plantN: plant,
    unitN: unitsNear,
    ready,
    readyLabel,
    soft: !!z.soft,
    livePlayer: "not_wired",
  };
}

/** Soft plant observe endpoints · devices with observe/thermal skills count as cams. */
export function softIsPlantCam(a) {
  if (!a || !a.ll) return false;
  if (a.kind === "camera") return true;
  if (a.kind === "device") {
    const skills = a.skills || [];
    if (skills.includes("observe") || skills.includes("thermal")) return true;
    if (a.fixed && /cam|ptz|eye|vision|udock/i.test(String(a.tier || a.label || a.id || ""))) {
      return true;
    }
  }
  return /cam|cam_|ptz|udock/i.test(String(a.id || "")) || /cam|ptz/i.test(String(a.label || ""));
}

function softPlantCamsNear(softState, ll, n) {
  const assets = (softState && softState.assets) || [];
  const cams = assets
    .filter((a) => softIsPlantCam(a))
    .map((a) => ({
      id: a.id,
      d: haversineM(ll, a.ll),
    }))
    .sort((a, b) => a.d - b.d)
    .slice(0, n)
    .map((x) => x.id);
  return cams;
}

/** Zone geo centre from ll · ring centroid · soft origin. */
export function softZoneOriginOf(zone, softState) {
  if (zone && Array.isArray(zone.ll) && zone.ll.length >= 2) return zone.ll.slice(0, 2);
  const ring = zone && zone.ring;
  if (Array.isArray(ring) && ring.length >= 3) {
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (const p of ring) {
      if (!p || p.length < 2) continue;
      sx += Number(p[0]);
      sy += Number(p[1]);
      n += 1;
    }
    if (n > 0) return [sx / n, sy / n];
  }
  if (softState && Array.isArray(softState.origin)) return softState.origin.slice(0, 2);
  return null;
}

/**
 * Run / Watch scope for active zone (pure).
 * Radius default ~95m covers Soft pad squares without thrashing the globe.
 */
export function softZoneRunScopeOf(zone, softState, opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  if (!zone) {
    return {
      ok: false,
      zoneId: "",
      label: "",
      origin: null,
      cameraIds: [],
      radiusM: 95,
      unitIds: [],
    };
  }
  const origin = softZoneOriginOf(zone, softState);
  const radius =
    Number.isFinite(Number(o.radiusM)) && Number(o.radiusM) > 0
      ? Number(o.radiusM)
      : 95;
  const assets = (softState && softState.assets) || [];
  const cameraIds = normalizeCamIds(
    (zone.cameraIds && zone.cameraIds.length
      ? zone.cameraIds
      : softPlantCamsNear(softState, origin, 4)) || [],
  );
  const unitIds = [];
  if (origin) {
    for (const a of assets) {
      if (!a || !a.ll || a.fixed || a.kind === "device") continue;
      if (haversineM(origin, a.ll) <= radius) unitIds.push(String(a.id));
    }
  }
  return {
    ok: true,
    zoneId: zone.id,
    label: zone.label || zone.id,
    soft: !!zone.soft,
    origin,
    cameraIds,
    radiusM: radius,
    unitIds,
    livePlayer: "not_wired",
  };
}

/** Jobs/incidents near zone for Run scope. */
export function softFilterNearZoneLl(items, origin, radiusM, getLl) {
  if (!origin || !Array.isArray(items)) return items || [];
  const r =
    Number.isFinite(Number(radiusM)) && Number(radiusM) > 0 ? Number(radiusM) : 120;
  const near = [];
  const far = [];
  for (const it of items) {
    const ll = getLl ? getLl(it) : null;
    if (ll && haversineM(origin, ll) <= r) near.push(it);
    else far.push(it);
  }
  /* Prefer in-zone · keep a few global so operator never sees empty ghost theatre. */
  if (near.length) return [...near, ...far.slice(0, 2)].slice(0, 16);
  return items.slice(0, 12);
}

function haversineM(a, b) {
  if (!a || !b) return 1e9;
  const R = 6371000;
  const toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b[1] - a[1]);
  const dLng = toR(b[0] - a[0]);
  const la1 = toR(a[1]);
  const la2 = toR(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** True when lng/lat is inside volume radius (no origin / bad ll = treat as in). */
export function softLngLatInRadius(ll, origin, radiusM) {
  if (!origin || !Array.isArray(origin) || origin.length < 2) return true;
  if (!ll) return true;
  const a = Array.isArray(ll)
    ? ll.length >= 2
      ? [Number(ll[0]), Number(ll[1])]
      : null
    : ll.lng != null && ll.lat != null
      ? [Number(ll.lng), Number(ll.lat)]
      : null;
  if (!a || !Number.isFinite(a[0]) || !Number.isFinite(a[1])) return true;
  const r =
    Number.isFinite(Number(radiusM)) && Number(radiusM) > 0 ? Number(radiusM) : 95;
  return haversineM(a, origin) <= r;
}

/** [[minLng,minLat],[maxLng,maxLat]] for MapLibre fitBounds · null if thin. */
export function softRingBoundsOf(ring) {
  if (!Array.isArray(ring) || ring.length < 2) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let n = 0;
  for (const p of ring) {
    if (!p || p.length < 2) continue;
    const x = Number(p[0]);
    const y = Number(p[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    n += 1;
  }
  if (n < 2 || minX === maxX || minY === maxY) return null;
  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

/**
 * Map floating plate + marker dimming model for active volume.
 */
export function softVolumeMapContextOf(zone, softState, opts) {
  const scope = softZoneRunScopeOf(zone, softState, opts);
  if (!scope.ok) {
    return { ok: false, label: "", coverN: 0, unitN: 0, plantN: 0, ready: "open" };
  }
  const vm = softZoneVolumeVmOf(
    {
      ...(zone || {}),
      id: scope.zoneId,
      label: scope.label,
      cameraIds: scope.cameraIds,
      ll: scope.origin,
      soft: scope.soft,
    },
    softState,
    (opts && opts.devices) || [],
  );
  return {
    ok: true,
    id: scope.zoneId,
    label: scope.label,
    origin: scope.origin,
    radiusM: scope.radiusM,
    coverN: vm.coverN,
    plantN: vm.plantN,
    unitN: scope.unitIds.length || vm.unitN,
    ready: vm.ready,
    readyLabel: vm.readyLabel,
    roleLabel: vm.roleLabel,
    tone: vm.tone,
    soft: !!scope.soft,
  };
}

function softRectRing(ll, half) {
  const [lng, lat] = ll;
  return [
    [lng - half, lat - half],
    [lng + half, lat - half],
    [lng + half, lat + half],
    [lng - half, lat + half],
    [lng - half, lat - half],
  ];
}

function normalizeCamIds(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const x of list) {
    const id = String(x || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out.slice(0, 12);
}

export function bindCameraToZone(zoneId, cameraId) {
  const z = String(zoneId || "").trim();
  const c = String(cameraId || "").trim();
  if (!z || !c || !isLocalHost()) return loadZoneCamBinds();
  const map = { ...loadZoneCamBinds() };
  const cur = normalizeCamIds(map[z] || []);
  if (!cur.includes(c)) cur.push(c);
  map[z] = cur.slice(0, 12);
  saveZoneCamBinds(map);
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("croc-zone-bind", { detail: { zoneId: z, cameraId: c } }));
    }
  } catch {
    /* */
  }
  return map;
}

export function unbindCameraFromZone(zoneId, cameraId) {
  const z = String(zoneId || "").trim();
  const c = String(cameraId || "").trim();
  if (!z || !isLocalHost()) return loadZoneCamBinds();
  const map = { ...loadZoneCamBinds() };
  map[z] = normalizeCamIds(map[z] || []).filter((id) => id !== c);
  saveZoneCamBinds(map);
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("croc-zone-bind", { detail: { zoneId: z, cameraId: c, unbind: true } }));
    }
  } catch {
    /* */
  }
  return map;
}

/**
 * Map thumbnail hosts — plant cameras that should show Soft mini Live.
 * Soft≠Live: Soft plant invent only on 127; production only shows bind hosts
 * that already exist Soft≠ invent Soft theatre thumbs.
 * @returns {{ id, ll, label, zoneId, soft, livePlayer }[]}
 */
export function softMapLiveThumbsOf({ zoneCatalog, activeZoneId, softState, devices }) {
  const active = String(activeZoneId || "").trim();
  const zones = Array.isArray(zoneCatalog) ? zoneCatalog : [];
  const localSoft = isLocalHost();
  /* Soft≠Live: ignore Soft fleet state off localhost Soft≠ invent Soft map thumbs. */
  const softPad = localSoft ? softState : null;
  const thumbs = [];
  const pushCam = (camId, zoneId, forceLl) => {
    const id = String(camId || "").trim();
    if (!id || thumbs.some((t) => t.id === id)) return;
    let ll = forceLl;
    let label = id;
    let groupId = "";
    let resolvedZoneId = zoneId || "";
    const softA = ((softPad && softPad.assets) || []).find((a) => a && a.id === id);
    if (softA && softA.ll) {
      ll = softA.ll;
      label = softA.label || id;
    } else {
      const d = (devices || []).find((x) => x && String(x.id) === id);
      if (d && d.ll) {
        ll = d.ll;
        label = d.label || id;
        groupId = String(d.group || "");
        resolvedZoneId = resolvedZoneId || String(d.zone || "");
      }
    }
    if (!ll) return;
    thumbs.push({
      id,
      ll,
      label,
      zoneId: resolvedZoneId,
      groupId,
      soft: true,
      livePlayer: "not_wired",
      aspect: "soft_preview",
      active: !!active && active === resolvedZoneId,
    });
  };

  for (const z of zones) {
    /* Cover binds: only localStorage Soft practice Soft≠ production formal bind. */
    const cams = localSoft ? (z.cameraIds || []) : [];
    if (active && z.id !== active && cams.length === 0) continue;
    const show = !active || z.id === active || (localSoft && z.soft && cams.length);
    if (!show) continue;
    for (const cam of cams) pushCam(cam, z.id, z.ll);
  }

  /* Soft plant cameras near active zone when binds empty — lab only. */
  if (active && softPad) {
    const z = zones.find((x) => x.id === active);
    const origin = (z && z.ll) || softZoneOriginOf(z, softPad) || softPad.origin;
    if (origin && (!z || !(z.cameraIds || []).length)) {
      softPlantCamsNear(softPad, origin, 4).forEach((id) => pushCam(id, active));
    }
  }

  /* Site-wide Soft plant thumbs when no zone focused · lab Soft only. */
  if (!active && softPad && thumbs.length === 0) {
    const ori = softPad.origin;
    if (ori) softPlantCamsNear(softPad, ori, 4).forEach((id) => pushCam(id, ""));
  }

  return thumbs.slice(0, 8);
}

/**
 * Zone command-space view-model for panel.
 */
export function softZoneOpsSpaceOf(zone, { softState, devices, catalogueCameras }) {
  if (!zone) {
    return { ok: false, honesty: "No zone selected · pick a fence or Zones desk" };
  }
  const binds = zone.cameraIds || [];
  const catalog = Array.isArray(catalogueCameras) ? catalogueCameras : [];
  const devicesL = Array.isArray(devices) ? devices : [];
  const softAssets = (softState && softState.assets) || [];

  const cams = binds.map((id) => {
    const soft = softAssets.find((a) => a && a.id === id);
    const live = devicesL.find((d) => d && String(d.id) === id);
    const cat = catalog.find((c) => c && (c.id === id || c.camera_id === id));
    return {
      id,
      label: (soft && soft.label) || (live && live.label) || (cat && (cat.label || cat.name)) || id,
      soft: !!soft || zone.soft,
      ll: (soft && soft.ll) || (live && live.ll) || null,
      livePlayer: "not_wired",
      honesty: "Practice map thumbnail · browser player not wired",
    };
  });

  return {
    ok: true,
    zone,
    cameras: cams,
    cameraCount: cams.length,
    plant: zone.deviceIds || [],
    volume: softZoneVolumeVmOf(zone, softState, devicesL),
    livePlayer: "not_wired",
    honesty: "Covers on map · player not connected",
    canBind: isLocalHost(),
  };
}

/**
 * Candidate cameras for bind UI — Live device cams + Soft plant.
 */
export function softBindCameraCandidates({ devices, softState, catalogue }) {
  const out = [];
  const seen = new Set();
  const add = (id, label, src) => {
    const i = String(id || "").trim();
    if (!i || seen.has(i)) return;
    seen.add(i);
    out.push({ id: i, label: label || i, src });
  };
  for (const d of devices || []) {
    if (!d) continue;
    const kind = String(d.kind || d.device_type || d.product_hw_code || "").toLowerCase();
    if (/cam|camera|udock|eye|vision/.test(kind) || /cam/i.test(d.id || "") || /cam/i.test(d.label || "")) {
      add(d.id, d.label, "live_device");
    }
  }
  for (const a of (softState && softState.assets) || []) {
    if (!a || !softIsPlantCam(a)) continue;
    add(a.id, a.label || a.tier || a.id, "soft");
  }
  for (const c of catalogue || []) {
    if (!c) continue;
    add(c.id || c.camera_id, c.label || c.name, "catalogue");
  }
  return out.slice(0, 40);
}
